import { Color, Spacing } from '@persona-id/design-tokens';
import { TrashIcon } from '@persona-id/design-tokens/icons';
import { keyBy, sortBy } from 'lodash/fp';
import { Fragment, useEffect, useMemo, VFC } from 'react';
import { Control, Controller, useFieldArray, useWatch } from 'react-hook-form';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import HandlebarFormControl from 'dashboard/components/HandlebarFormControl';
import SelectOption from 'dashboard/components/JsonSchemaSelect/SelectOption';
import ReactSelect from 'dashboard/components/ReactSelect';
import Tooltip from 'dashboard/components/Tooltip';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { ObjectPropertiesSchema } from 'dashboard/models/external-integration';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { JsonSchemaCustomTypes } from 'lib/fields';
import { JsonSchema, JsonSchemaTypes } from 'lib/json-schema';
import { useFormSecurely } from 'lib/security/reactHookForm';
import { intersperse } from 'lib/utils/array';

const KeyValueRowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  &:not(:last-child) {
    margin-bottom: ${Spacing[2]};
  }
`;

const Well = styled.div`
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[4]};
`;

const KeyValueContainer = styled.div`
  dispaly: flex;
  flex-direction: column;
`;

const KeyValueRowKey = styled.div`
  flex-shrink: 0;
  width: 45%;
`;
const KeyValueRowValue = styled.div`
  flex-shrink: 0;
  width: 45%;
`;

const KeyValueDeleteButton = styled.div`
  margin-left: ${Spacing[1]};
`;

const AddKeyValueButton = styled.div`
  display: inline-block;
  margin-top: ${Spacing[2]};
  margin-left: ${Spacing[2]};
`;
const BottomRowContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-content: space-around;
`;

interface KeyValueRowProps {
  control: Control<{
    keyValue: ObjectKeyValue[];
  }>;
  editable: boolean;
  idPrefix: string;
  index: number;
  keyOptions: {
    label: string;
    value: string;
    property: string;
    schema: { description?: string; type?: string };
  }[];
  onDelete: () => void;
  jsonSchema: JsonSchema;
  tokensToDisplayValue: Record<string, string>;
  optionsMap: Record<string, ObjectPropertiesSchema>;
}

const KeyValueRow: VFC<KeyValueRowProps> = ({
  control,
  editable,
  idPrefix,
  index,
  keyOptions,
  onDelete,
  jsonSchema,
  tokensToDisplayValue,
  optionsMap,
}) => {
  const allKeys = keyBy('key', useWatch({ control, name: `keyValue` }));
  const keyName = useWatch({ control, name: `keyValue.${index}.key` });
  const requiredField = optionsMap[keyName]?.required;
  const valueOptions = optionsMap[keyName]?.options;
  const description = optionsMap[keyName]?.description;
  const objectType = optionsMap[keyName]?.objectType;

  const valueHandleBar = (
    <Controller
      control={control}
      name={`keyValue.${index}.value`}
      rules={{
        required: false,
      }}
      render={({ field: { value, onChange } }) => (
        <HandlebarFormControl
          size='md'
          type='input'
          key={`header-value-form-${index}`}
          value={value}
          aria-label={`Input of value ${index + 1}`}
          updateValue={onChange}
          editable={editable}
          allowedTypes={[JsonSchemaTypes.String, JsonSchemaCustomTypes.Date]}
          jsonSchema={jsonSchema}
          tokensToDisplayValue={tokensToDisplayValue}
          placeholder='Select Value'
          id={`${idPrefix}__value-input-${index}`}
        />
      )}
    />
  );

  const valueSelector = (
    <Controller
      control={control}
      name={`keyValue.${index}.value`}
      rules={{
        required: false,
      }}
      render={({ field: { value, onChange } }) => (
        <ReactSelect
          key={`header-value-selector-${index}`}
          value={value}
          aria-label={`Selector of value ${index + 1}`}
          onChange={onChange}
          placeholder='Select Value'
          id={`${idPrefix}__value-selector-${index}`}
          options={valueOptions}
          portal
        />
      )}
    />
  );

  return (
    <KeyValueContainer>
      <KeyValueRowContainer>
        <KeyValueRowKey>
          <Controller
            control={control}
            name={`keyValue.${index}.key`}
            rules={{
              required: true,
            }}
            render={({ field: { value, onChange } }) =>
              requiredField ? (
                <Input type='text' value={optionsMap[value].label} readOnly={true} />
              ) : (
                <ReactSelect
                  aria-label={`Selector of key ${index + 1}`}
                  disabled={!editable}
                  id={`${idPrefix}__key-${index}`}
                  options={keyOptions.filter((opt) => {
                    return opt.value === value || !(opt.value in allKeys);
                  })}
                  placeholder='Select or Search Property'
                  portal
                  value={value}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  components={
                    description || objectType
                      ? {
                          Option: SelectOption,
                        }
                      : {}
                  }
                  onChange={onChange}
                />
              )
            }
          />
        </KeyValueRowKey>
        <KeyValueRowValue>
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          {valueOptions?.length > 0 ? valueSelector : valueHandleBar}
        </KeyValueRowValue>
        <Tooltip
          trigger='hover'
          tooltip='Field required for verification, cannot be deleted'
          disabled={!requiredField}
          hideArrow={true}
          delayHide={1000}
        >
          <KeyValueDeleteButton>
            <Button
              aria-label={`Delete key value pair ${index + 1}`}
              disabled={!editable || requiredField}
              id={`${idPrefix}__delete-${index}`}
              type='button'
              variant='basic'
              onClick={onDelete}
            >
              <TrashIcon />
            </Button>
          </KeyValueDeleteButton>
        </Tooltip>
      </KeyValueRowContainer>
    </KeyValueContainer>
  );
};

interface ObjectKeyValue {
  key: string;
  value: string;
}

const NEW_KEY_VALUE: ObjectKeyValue = {
  key: '',
  value: '',
};
interface OptionsFieldMappingsPanelProps {
  editable: boolean;
  keyValueObject: Record<string, string>;
  title: string;
  idPrefix: string;
  updateObject: (keyValueObject: Record<string, string>) => void;
  jsonSchema: JsonSchema;
  tokensToDisplayValue: Record<string, string>;
  options: ObjectPropertiesSchema[];
  onOutsideClick: () => void;
}

const OptionsFieldMappingsPanel: VFC<OptionsFieldMappingsPanelProps> = ({
  editable,
  keyValueObject,
  updateObject,
  title,
  idPrefix,
  jsonSchema,
  tokensToDisplayValue,
  options = [],
  onOutsideClick,
}) => {
  const transformedKeyValue = useMemo(
    () => Object.entries(keyValueObject).map(([k, v]) => ({ key: k, value: v })),
    [keyValueObject],
  );
  const { control, getValues, handleSubmit, watch } = useFormSecurely<{
    keyValue: ObjectKeyValue[];
  }>({
    defaultValues: {
      keyValue: transformedKeyValue.length > 0 ? transformedKeyValue : [NEW_KEY_VALUE],
    },
  });
  const { append, fields, remove } = useFieldArray({ control, name: 'keyValue' });

  useEffect(() => {
    const subscription = watch(() => {
      handleSubmit((form) => {
        updateObject(Object.fromEntries(form.keyValue.map((kv) => [kv.key, kv.value])));
      })();
    });
    return () => subscription.unsubscribe();
  }, [watch, handleSubmit, updateObject]);

  const [sortedOptions, optionsMap] = useMemo(
    () => [
      sortBy(
        'label',
        options?.map((opt) => {
          return {
            label: opt.label,
            value: opt.value,
            property: opt.label,
            schema: { description: opt.description, type: opt.objectType },
          };
        }),
      ),
      keyBy('value', options),
    ],
    [options],
  );

  const usedOptions = keyBy('key', useWatch({ control, name: `keyValue` }));
  const unusedFieldOptions = useMemo(
    () => options.filter((opt) => !(opt.value in usedOptions)),
    [usedOptions, options],
  );

  const rows = fields.map((field, index) => (
    <KeyValueRow
      key={field.id}
      control={control}
      editable={editable}
      idPrefix={idPrefix}
      index={index}
      keyOptions={sortedOptions}
      onDelete={() => {
        remove(index);
      }}
      jsonSchema={jsonSchema}
      tokensToDisplayValue={tokensToDisplayValue}
      optionsMap={optionsMap}
    />
  ));

  return (
    <MaximizablePanel
      outsideExceptionalIdPrefixes={[idPrefix]}
      title={title}
      onOutsideClickAndEscPress={onOutsideClick}
    >
      <Well>
        {getValues().keyValue.length > 0 ? (
          <>
            {intersperse(rows, (i) => (
              <Fragment key={`separator-${i}`}>
                <Spacer height={1} />
              </Fragment>
            ))}
          </>
        ) : (
          <Paragraph size='md'>Please add at least one property </Paragraph>
        )}
        <BottomRowContainer>
          {unusedFieldOptions.length > 0 ? (
            <AddKeyValueButton>
              <Button
                aria-label='Add a key value'
                disabled={!editable}
                id={`${idPrefix}__key-value-add`}
                variant='basic'
                onClick={() => append({ key: unusedFieldOptions[0].value, value: '' })}
              >
                Add Property
              </Button>
            </AddKeyValueButton>
          ) : null}
        </BottomRowContainer>
      </Well>
    </MaximizablePanel>
  );
};

export default OptionsFieldMappingsPanel;
